import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Carousel from '@nerdwallet/react-carousel';
import classNames from 'classnames';

import { CenteredContainer, Box, Button, Title } from '@nerdwallet/currency';
import Right from '@nerdwallet/react-icon/gizmo/Right';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import _ from 'lodash/';

import {
  repeatable,
  boolCheckboxSpec,
  stringSelectSpec,
  stringInputSpec,
} from '../../lib/editing-configs';
import getTextColor from '../../lib/text-color-config';

import BackgroundColor from '../background-color/background-color';
import parseStyleFormatting from '../../lib/parse-style-formatting';

import FlexCardItem from './flex-card-item/flex-card-item';
import {
  CARDS_PER_ROW_ENUM,
  CARD_TREATMENT_ENUM,
  ALIGNMENT_ENUM,
} from './lib/constants';

import './flex-card.less';

const setRowStyle = (cardsPerRow, cards, size) => {
  const total = Math.ceil(cards.length / parseInt(cardsPerRow, 10));
  if (size === 'md' && cardsPerRow === '4') {
    return {
      gridTemplateColumns: 'repeat(3, 1fr)',
      gridTemplateRows: `repeat(${Math.ceil(cards.length / 3)}, 1fr)`,
    };
  }
  if (size === 'sm') {
    return {
      gridTemplateColumns: `repeat(1, 1fr)`,
      gridTemplateRows: `repeat(${cards.length}, 1fr)`,
    };
  }
  return {
    gridTemplateColumns: `repeat(${cardsPerRow}, 1fr)`,
    gridTemplateRows: `repeat(${total}, 1fr)`,
  };
};

const titleSizeEnum = {
  'x-large': 'default',
  large: 'small',
};

const NextSlide = ({ onClick, arrowColor, className }) => {
  const classStyle = className.replace('slick-arrow slick-next', '');
  useEffect(() => {
    const [activeCarouselDot] = document.querySelectorAll(
      'li.slick-active button'
    );
    if (activeCarouselDot) {
      activeCarouselDot.style.setProperty('--activeDot', arrowColor);
    }
  }, [arrowColor, onClick]);

  return (
    <Button
      className={classNames([classStyle, 'flex-card-list-carousel-arrow'])}
      tertiary
      onClick={onClick}
    >
      <Right size={30} color={arrowColor} />
    </Button>
  );
};

const FlexCard = ({
  arrowColor,
  stacked,
  size,
  cardsPerRow,
  cardTreatment,
  borderColorTreatment,
  borderSizeTreatment,
  desktopCarousel,
  cardsToPreview,
  shadowColorTreatment,
  title,
  titleInChronicle,
  titleAlignment,
  bgOptions,
  cards,
  titleSize,
  wideSizeLayout,
  textColorOptions,
}) => (
  <BackgroundColor bgOptions={bgOptions}>
    {(backgroundColor) => {
      const { textColor } = getTextColor(textColorOptions);
      const colorStyle = { color: textColor };
      const rowStyle = setRowStyle(cardsPerRow, cards, size);
      const titleAlignmentValue =
        size !== 'sm' ? titleAlignment : ALIGNMENT_ENUM.LEFT;
      const mobileStackedOrWide =
        (size !== 'sm' && !desktopCarousel) || (size === 'sm' && stacked);
      const mobileCarousel = size === 'sm' && !stacked;
      const isDesktopCarousel = desktopCarousel && size !== 'sm';
      return (
        <div style={{ backgroundColor }}>
          <CenteredContainer
            className={classNames([
              'flex-card-wrapper',
              {
                carousel: (size === 'sm' && !stacked) || desktopCarousel,
                'oversize-layout': !wideSizeLayout || isDesktopCarousel,
              },
            ])}
          >
            {title && (
              <Box
                display="flex"
                paddingBottom={4}
                justifyContent="space-between"
                width="100"
              >
                <Title
                  size={titleSizeEnum[titleSize]}
                  style={colorStyle}
                  align={titleAlignmentValue}
                  className={classNames('flex-card-wrapper-title', {
                    chronicle: titleInChronicle,
                  })}
                >
                  {parseStyleFormatting(title)}
                </Title>
              </Box>
            )}
            {mobileStackedOrWide && (
              <Box style={rowStyle} className="flex-card-list-container">
                {cards.map((item) => (
                  <FlexCardItem
                    cardTreatment={cardTreatment}
                    borderColorTreatment={borderColorTreatment}
                    borderSizeTreatment={borderSizeTreatment}
                    shadowColorTreatment={shadowColorTreatment}
                    cardsPerRow={cardsPerRow}
                    size={size}
                    textColor={colorStyle}
                    {...item}
                  />
                ))}
              </Box>
            )}
            {mobileCarousel && (
              <Carousel
                id="nerd-carousel"
                autoplay={false}
                allowLooping
                decorateNav={false}
                enableDotNavigation
                dots
                nextArrow={<NextSlide arrowColor={arrowColor} />}
                dotsClass="slick-dots flex-card-list-dots"
                slidesToShow="1.15"
                className="flex-card-list-container"
              >
                {cards.map((item) => (
                  <FlexCardItem
                    cardTreatment={cardTreatment}
                    borderColorTreatment={borderColorTreatment}
                    borderSizeTreatment={borderSizeTreatment}
                    shadowColorTreatment={shadowColorTreatment}
                    cardsPerRow={cardsPerRow}
                    size={size}
                    textColor={colorStyle}
                    {...item}
                  />
                ))}
              </Carousel>
            )}
            {isDesktopCarousel && (
              <Carousel
                id="nerd-carousel"
                autoplay={false}
                allowLooping
                decorateNav={false}
                enableDotNavigation
                dots
                dotsClass="slick-dots flex-card-list-dots"
                slidesToShow={cardsToPreview || '3'}
                className="flex-card-list-container"
              >
                {cards.map((item) => (
                  <FlexCardItem
                    cardTreatment={cardTreatment}
                    desktopCarousel={desktopCarousel}
                    borderColorTreatment={borderColorTreatment}
                    borderSizeTreatment={borderSizeTreatment}
                    shadowColorTreatment={shadowColorTreatment}
                    cardsPerRow={cardsToPreview}
                    size={size}
                    textColor={colorStyle}
                    {...item}
                  />
                ))}
              </Carousel>
            )}
          </CenteredContainer>
        </div>
      );
    }}
  </BackgroundColor>
);

FlexCard.getEditingConfig = (componentDocument) => {
  const treatmentShadowOptions =
    _.get(componentDocument, 'props.cardTreatment', '') ===
    CARD_TREATMENT_ENUM.BOX_SHADOW
      ? [
          stringInputSpec(
            'shadowColorTreatment',
            'Card Drop Shadow Color Hex (eg: "#fff" - default is #cdcdcd)'
          ),
        ]
      : '';
  const treatmentBorderOptions =
    _.get(componentDocument, 'props.cardTreatment', '') ===
    CARD_TREATMENT_ENUM.BORDER
      ? [
          stringInputSpec(
            'borderColorTreatment',
            'Card Border Color Hex (eg: "#fff" - default is #cdcdcd)'
          ),
          stringInputSpec(
            'borderSizeTreatment',
            'Card Border Width (in px, eg: "2px" - default is 1px)'
          ),
        ]
      : '';
  const cardsAmountConfig = componentDocument?.props?.desktopCarousel
    ? [
        stringSelectSpec(
          'cardsToPreview',
          'Cards to preview?',
          [
            { label: 'Select an option', value: '' },
            { label: '4', value: CARDS_PER_ROW_ENUM.FOUR },
            { label: '3', value: CARDS_PER_ROW_ENUM.THREE },
            { label: '2', value: CARDS_PER_ROW_ENUM.TWO },
            { label: '1', value: CARDS_PER_ROW_ENUM.ONE },
          ],
          {
            help: 'Defines # of cards displayed in desktop carousel preview',
          }
        ),
      ]
    : [
        stringSelectSpec(
          'cardsPerRow',
          'Cards per Row',
          [
            { label: '4', value: CARDS_PER_ROW_ENUM.FOUR },
            { label: '3', value: CARDS_PER_ROW_ENUM.THREE },
            { label: '2', value: CARDS_PER_ROW_ENUM.TWO },
            { label: '1', value: CARDS_PER_ROW_ENUM.ONE },
          ],
          {
            help: '4 colums layout is only available in desktop, on tablet defaults to 3 colums maximum',
          }
        ),
        boolCheckboxSpec(
          'wideSizeLayout',
          'Display cards in a wider layout? (desktop/tablet only)'
        ),
      ];
  return [
    stringInputSpec('title', 'Cards List Title', {
      help: 'For GothamBlack font, surround title text with | like |this|',
    }),
    stringSelectSpec('titleSize', 'Cards List Title Size', [
      { label: 'Default', value: 'x-large' },
      { label: 'Small', value: 'large' },
    ]),
    boolCheckboxSpec('titleInChronicle', 'Title in Chronicle?'),
    stringSelectSpec(
      'titleAlignment',
      'Select Title Alignment',
      [
        { label: 'Left', value: ALIGNMENT_ENUM.LEFT },
        { label: 'Center', value: ALIGNMENT_ENUM.CENTER },
        { label: 'Right', value: ALIGNMENT_ENUM.RIGHT },
      ],
      {
        help: 'Add a border style for the card',
      }
    ),
    boolCheckboxSpec('desktopCarousel', 'Display cards in carousel? (Desktop)'),
    ...cardsAmountConfig,
    boolCheckboxSpec('stacked', 'Display cards without carousel? (Mobile)'),
    stringInputSpec(
      'arrowColor',
      'Mobile Carousel Arrow Color (eg: "#fff" - default is #008255)'
    ),
    ...getTextColor.getEditingConfig(componentDocument),
    ...BackgroundColor.getEditingConfig(componentDocument),
    stringSelectSpec(
      'cardTreatment',
      'Select Card Treatment',
      [
        { label: 'None', value: CARD_TREATMENT_ENUM.NONE },
        { label: 'Box Shadow', value: CARD_TREATMENT_ENUM.BOX_SHADOW },
        { label: 'Border', value: CARD_TREATMENT_ENUM.BORDER },
      ],
      {
        help: 'Add a border style for the card',
      }
    ),
    ...treatmentShadowOptions,
    ...treatmentBorderOptions,
    repeatable('cards', 'Card', 'Card', (i) =>
      FlexCardItem.getEditingConfig({
        props: componentDocument.props?.cards[i],
      })
    ),
  ];
};

FlexCard.displayName = 'Hero Video';

export default connect(
  createStructuredSelector({
    size: _.property('mediaQuery'),
  })
)(FlexCard);

NextSlide.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
  arrowColor: PropTypes.string,
};

FlexCard.propTypes = {
  cards: PropTypes.array,
  stacked: PropTypes.bool,
  size: PropTypes.string,
  cardsPerRow: PropTypes.string,
  desktopCarousel: PropTypes.bool,
  ...BackgroundColor.propTypes,
  cardTreatment: PropTypes.string,
  borderColorTreatment: PropTypes.string,
  borderSizeTreatment: PropTypes.string,
  shadowColorTreatment: PropTypes.string,
  title: PropTypes.string,
  titleSize: PropTypes.string,
  titleInChronicle: PropTypes.bool,
  titleAlignment: PropTypes.string,
  arrowColor: PropTypes.string,
  wideSizeLayout: PropTypes.bool,
  cardsToPreview: PropTypes.string,
};

FlexCard.defaultProps = {
  cards: [],
  cardsPerRow: CARDS_PER_ROW_ENUM.FOUR,
  cardsToPreview: CARDS_PER_ROW_ENUM.THREE,
  stacked: false,
  title: 'Flex Card Title',
  cardTreatment: CARD_TREATMENT_ENUM.NONE,
  shadowColorTreatment: '#cdcdcd',
  ...BackgroundColor.defaultProps,
  borderColorTreatment: '#cdcdcd',
  borderSizeTreatment: '1px',
  titleSize: 'x-large',
  arrowColor: '#008255',
  parseLinks: true,
};
